/**
 * The signed-in app's pages.
 *
 * Listed as values rather than a bare union so a page name read from outside
 * the type system — a URL, above all — can be checked at runtime. It lives
 * here rather than beside the sidebar that draws most of them because the
 * route table reads it too, and the sidebar renders links built from that
 * table: kept in `Sidebar.tsx`, the two would import each other.
 */
export const PAGE_NAMES = [
    "dashboard",
    "performance",
    "recentBookings",
    "forecast",
    "demandForecast",
    "segmentChannel",
    "marketConditions",
    "studyArchive",
    "settings",
    "strategies",
    "drafts",
    "users",
    "priceDraftsAdmin",
    "wirePaymentsAdmin",
    "connectionsAdmin",
    "activityAdmin",
    "strategyHelpAdmin",
    "supportRemindersAdmin",
    "featuresAdmin",
    "connections",
    "onboarding",
    "account",
] as const;

export type PageName = (typeof PAGE_NAMES)[number];

export function isPageName(value: string): value is PageName {
    return (PAGE_NAMES as readonly string[]).includes(value);
}

/**
 * The platform-admin consoles. They read every tenant and never the property
 * open, so the startup checks let an administrator reach them whatever state
 * that property is in (`useDashboardInit`), and a property opened in another
 * tab leaves them on screen (`App.tsx`).
 */
export const ADMIN_PAGE_NAMES: readonly PageName[] = [
    "users",
    "priceDraftsAdmin",
    "wirePaymentsAdmin",
    "connectionsAdmin",
    "activityAdmin",
    "strategyHelpAdmin",
    "supportRemindersAdmin",
    "featuresAdmin",
];

export function isAdminPage(page: PageName): boolean {
    return ADMIN_PAGE_NAMES.includes(page);
}
